/**
 * The toast queue — a module-level store with a `useSyncExternalStore`-shaped subscription.
 *
 * It lives **outside** React on purpose. The single most valuable place to fire a toast is an
 * axios interceptor or a mutation's `onError` callback, and neither of those is a component, so
 * neither can call a hook. A context-only toast API forces every one of those call sites to thread
 * a `showError` function down from a provider, which is exactly the plumbing that stopped apps
 * from reporting their failures at all. Here, `toast.error(...)` works from any module; the
 * provider is only the thing that paints the queue.
 *
 * The queue is capped (see `MAX_VISIBLE`) so a burst of failures cannot cover the page, and so
 * toasts emitted before any provider mounts cannot pile up unboundedly.
 */

import type { Toast, ToastOptions, ToastVariant } from './types';

/** How many toasts can be on screen at once; the oldest is evicted past this. */
export const MAX_VISIBLE = 4;

/** Default lifetime per variant. An error gets longer because it is the one worth reading. */
const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 3000,
  error: 5000,
  warning: 4500,
  info: 3500,
};

type Listener = (toasts: Toast[]) => void;

const listeners = new Set<Listener>();
let queue: Toast[] = [];
let sequence = 0;

function publish(next: Toast[]): void {
  queue = next;
  listeners.forEach((listener) => listener(queue));
}

/** Subscribe to queue changes. Returns the unsubscribe function. */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The current queue. Stable by reference between changes, so it is safe as a snapshot. */
export function getToasts(): Toast[] {
  return queue;
}

/**
 * Push a toast. Returns its id so a caller can dismiss it early.
 *
 * A blank message is dropped rather than rendered as an empty pill — the usual cause is an error
 * object whose `message` was undefined, and an empty toast tells the user strictly less than none.
 */
export function push(
  variant: ToastVariant,
  message: string,
  { duration, dedupe = true }: ToastOptions = {},
): string {
  const text = message.trim();
  if (!text) return '';

  if (dedupe) {
    const existing = queue.find((item) => item.variant === variant && item.message === text);
    // Re-publishing a new array with the same ids restarts the item's timer (see ToastItem's
    // effect), which is the behaviour we want: the repeat refreshes the toast rather than
    // stacking a second identical one.
    if (existing) {
      publish(queue.map((item) => (item.id === existing.id ? { ...item } : item)));
      return existing.id;
    }
  }

  sequence += 1;
  const toast: Toast = {
    id: `toast-${String(sequence)}`,
    variant,
    message: text,
    duration: duration ?? DEFAULT_DURATION[variant],
  };
  publish([...queue, toast].slice(-MAX_VISIBLE));
  return toast.id;
}

/** Remove one toast by id. A no-op if it already dismissed itself. */
export function dismiss(id: string): void {
  if (!queue.some((item) => item.id === id)) return;
  publish(queue.filter((item) => item.id !== id));
}

/**
 * Clear the whole queue. Call it on sign-out or on a hard route change, where the toasts describe
 * a screen the user is no longer looking at.
 */
export function dismissAll(): void {
  if (queue.length === 0) return;
  publish([]);
}
